import { useMemo } from 'react';
import SocialIcon from '../components/socialLogin/Icons';
import { useContext } from '../context';
import type { AuthWalletConnectorProperties } from '../types/properties';
import { useConnections } from './useConnections';
import { useLastConnectorId } from './useLastConnectorId';
import useWalletsProps, { type WalletProps } from './useWalletsProps';

export type WalletConnection = {
  accounts: readonly [string, ...string[]];
  chainId: number;
  walletProps: WalletProps;
};

export const useWalletConnections = (): WalletConnection[] => {
  const connections = useConnections();
  const wallets = useWalletsProps();
  const { config } = useContext();
  const { lastConnectorId } = useLastConnectorId();

  const walletConnections = useMemo(() => {
    const props: WalletConnection[] = [];
    for (const connection of connections) {
      const id = connection.connector.id;
      const wallet = wallets.find(
        (wallet) =>
          wallet.id === id ||
          wallet.groupId
            ?.split(',')
            .map((item) => item.trim())
            .includes(id)
      );
      if (!wallet) {
        continue;
      }

      if (connection.connector.type === 'particleAuth') {
        const authType = config
          .getWalletConnector<AuthWalletConnectorProperties>('particleAuth')
          ?.auth.getLatestAuthType();
        if (authType) {
          wallet.icon = <SocialIcon authType={authType} />;
        }
      } else {
        // 移动端wallet都是通过WalletConnect链接的，但是icon需要显示具体点击的wallet
        if (wallet.connector.id === 'walletConnect' && lastConnectorId && lastConnectorId !== wallet.connector.id) {
          const lastWallet = wallets.find(
            (wallet) =>
              wallet.id === lastConnectorId ||
              wallet.groupId
                ?.split(',')
                .map((item) => item.trim())
                .includes(lastConnectorId)
          );
          if (lastWallet?.icon) {
            wallet.icon = lastWallet.icon;
          }
        }
      }

      props.push({
        accounts: connection.accounts,
        chainId: connection.chainId,
        walletProps: wallet,
      });
    }

    return props;
  }, [connections, wallets, config, lastConnectorId]);

  return walletConnections;
};
